import { Deposit, TransactionResponse } from '@notabene/javascript-sdk';
import { MoneyScaled } from '@quickbit/qb-design-system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchAPI } from 'api/fetchAPI';
import { environment } from 'utils/environmentVariables';

export interface DestinationInfo {
  amount: MoneyScaled;
  destinationAddress: string;
  customerToken: string;
  euroAmount: MoneyScaled;
}

export interface OriginatorInfo {
  amount: MoneyScaled;
  sourceAddress: string;
  asset: string;
  customerToken: string;
  notabeneTransactionId: string | null;
  euroAmount: MoneyScaled;
}

export const getDestinationInfo = async (
  sendPreviewId: string
): Promise<DestinationInfo> => {
  const data = await fetchAPI<DestinationInfo>(
    `travel-rule-info/${sendPreviewId}`
  );
  return data;
};

export const useGetDestinationInfo = (sendPreviewId: string | undefined) => {
  return useQuery({
    queryKey: ['destinationInfo', sendPreviewId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getDestinationInfo(sendPreviewId!),
    enabled: !!sendPreviewId,
  });
};

export const getDestinationInfoForContact = async (
  contactId: string
): Promise<DestinationInfo> => {
  const data = await fetchAPI<DestinationInfo>(
    `travel-rule-info/contact/${contactId}`
  );
  return data;
};

export const addCollectedData = async (
  sendPreviewId: string,
  data: Record<string, any>
) => {
  return fetchAPI<object>(
    `travel-rule-info/${sendPreviewId}/add-collected-data`,
    {
      method: 'POST',
      body: { data },
      dontModifyCasing: true,
    }
  );
};

export const addCollectedDataToContact = async (
  contactId: string,
  data: Record<string, any>
) => {
  return fetchAPI<object>(
    `travel-rule-info/contact/${contactId}/add-collected-data`,
    {
      method: 'POST',
      body: { data },
      dontModifyCasing: true,
    }
  );
};

export const uploadProofOfOwnershipForTransaction = async (
  transactionId: string,
  files: File[]
): Promise<void> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const BASE_URL = environment.REACT_APP_BASE_URL;
  const response = await fetch(
    `${
      BASE_URL ? BASE_URL + '/' : ''
    }travel-rule-info/originator/${transactionId}/proof-of-ownership`,
    {
      method: 'POST',
      body: formData,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Upload failed with status: ${response.status}`);
  }
};

export const useUploadProofOfOwnershipForTransaction = () => {
  return useMutation({
    mutationFn: async ({
      transactionId,
      files,
    }: {
      transactionId: string;
      files: File[];
    }) => {
      await uploadProofOfOwnershipForTransaction(transactionId, files);
    },
  });
};

export const uploadProofOfOwnershipForContact = async (
  contactId: string,
  files: File[]
): Promise<void> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const BASE_URL = environment.REACT_APP_BASE_URL;
  const response = await fetch(
    `${
      BASE_URL ? BASE_URL + '/' : ''
    }travel-rule-info/contact/${contactId}/proof-of-ownership`,
    {
      method: 'POST',
      body: formData,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Upload failed with status: ${response.status}`);
  }
};

export const useUploadProofOfOwnershipForContact = () => {
  return useMutation({
    mutationFn: async ({
      contactId,
      files,
    }: {
      contactId: string;
      files: File[];
    }) => {
      await uploadProofOfOwnershipForContact(contactId, files);
    },
  });
};

export const uploadProofOfOwnershipForSendPreview = async (
  sendOrderPreviewId: string,
  files: File[]
): Promise<void> => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const BASE_URL = environment.REACT_APP_BASE_URL;
  const response = await fetch(
    `${
      BASE_URL ? BASE_URL + '/' : ''
    }travel-rule-info/${sendOrderPreviewId}/proof-of-ownership`,
    {
      method: 'POST',
      body: formData,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`Upload failed with status: ${response.status}`);
  }
};

export const useUploadProofOfOwnershipForSendPreview = () => {
  return useMutation({
    mutationFn: async ({
      sendOrderPreviewId,
      files,
    }: {
      sendOrderPreviewId: string;
      files: File[];
    }) => {
      await uploadProofOfOwnershipForSendPreview(sendOrderPreviewId, files);
    },
  });
};

export const getOriginatorInfo = async (
  transactionId: string
): Promise<OriginatorInfo> => {
  const data = await fetchAPI<OriginatorInfo>(
    `travel-rule-info/originator/${transactionId}`
  );
  return data;
};

export const useGetOriginatorInfo = (transactionId: string | undefined) => {
  return useQuery({
    queryKey: ['originatorInfo', transactionId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getOriginatorInfo(transactionId!),
    enabled: !!transactionId,
  });
};

export const useAddCollectedOriginatorData = () => {
  return useMutation({
    mutationFn: async ({
      transactionId,
      data,
    }: {
      transactionId: string;
      data: TransactionResponse<Deposit>;
    }): Promise<void> => {
      await fetchAPI(
        `travel-rule-info/originator/${transactionId}/add-collected-data`,
        {
          method: 'POST',
          body: { data },
          dontModifyCasing: true,
        }
      );
    },
  });
};
